import { defineStore } from "pinia";
import { ref, computed } from "vue";
import studentClassApi, {
    type StudentClass,
    type StudentClassFilters,
} from "@/api/studentClass";
import NotificationService from "@/utils/notifications";

export const useStudentClassStore = defineStore("studentClass", () => {
    // State
    const studentClasses = ref<StudentClass[]>([]);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 1);
    const currentPage = computed(() => meta.value?.current_page || 1);
    const totalItems = computed(() => meta.value?.total || 0);

    // Actions
    const fetchStudentClasses = async (filters?: StudentClassFilters) => {
        try {
            loading.value = true;
            error.value = null;

            const params = {
                page: page.value,
                per_page: perPage.value,
                ...filters,
            };

            const response = await studentClassApi.getAll(params);
            studentClasses.value = response.data?.items || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch student classes";
            if (error.value) {
                NotificationService.error(error.value);
            }
        } finally {
            loading.value = false;
        }
    };

    const fetchStudentClassById = async (id: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.getById(id);
            return response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch student class";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return null;
        } finally {
            loading.value = false;
        }
    };

    const createStudentClass = async (data: any) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.create(data);

            if (response.success) {
                NotificationService.success(
                    "Student class enrollment created successfully",
                );
                await fetchStudentClasses();
                return response.data;
            } else {
                NotificationService.error(response.message);
                throw new Error(response.message);
            }
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.message ||
                "Failed to create student class enrollment";
            error.value = errorMessage;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateStudentClass = async (id: string, data: any) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.update(id, data);

            if (response.success) {
                NotificationService.success(
                    "Student class enrollment updated successfully",
                );
                await fetchStudentClasses();
                return response.data;
            } else {
                NotificationService.error(response.message);
                throw new Error(response.message);
            }
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.message ||
                "Failed to update student class enrollment";
            error.value = errorMessage;
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteStudentClass = async (id: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.delete(id);

            if (response.success) {
                NotificationService.success(
                    "Student class enrollment deleted successfully",
                );
                await fetchStudentClasses();
                return true;
            } else {
                error.value = response.message;
                NotificationService.error(response.message);
                return false;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to delete student class enrollment";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return false;
        } finally {
            loading.value = false;
        }
    };

    const activateStudentClass = async (id: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.activate(id);

            if (response.success) {
                NotificationService.success(
                    "Student class enrollment activated successfully",
                );
                await fetchStudentClasses();
                return response.data;
            } else {
                error.value = response.message;
                NotificationService.error(response.message);
                return null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to activate student class enrollment";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return null;
        } finally {
            loading.value = false;
        }
    };

    const deactivateStudentClass = async (id: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.deactivate(id);

            if (response.success) {
                NotificationService.success(
                    "Student class enrollment deactivated successfully",
                );
                await fetchStudentClasses();
                return response.data;
            } else {
                error.value = response.message;
                NotificationService.error(response.message);
                return null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to deactivate student class enrollment";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getStudentClassesByStudent = async (studentId: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.getByStudent(studentId);
            return response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch student enrollments";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return [];
        } finally {
            loading.value = false;
        }
    };

    const getStudentClassesByClass = async (classId: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.getByClass(classId);
            return response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch class enrollments";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return [];
        } finally {
            loading.value = false;
        }
    };

    const getStudentClassesBySemester = async (semesterId: string) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.getBySemester(semesterId);
            return response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch semester enrollments";
            if (error.value) {
                NotificationService.error(error.value);
            }
            return [];
        } finally {
            loading.value = false;
        }
    };

    /**
     * Bulk create student class enrollments.
     * Note: Toast notifications are handled by the component to allow
     * for custom messages with success/error counts.
     */
    const bulkCreateStudentClasses = async (data: {
        class_id: string;
        semester_id: string;
        enrollment_date: string;
        is_active?: boolean;
        student_ids: string[];
    }) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.bulkCreate(data);

            if (response.success) {
                await fetchStudentClasses();
                return response.data;
            } else {
                error.value = response.message;
                return null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to create bulk student class enrollments";
            return null;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Import student class enrollments from Excel/CSV file.
     */
    const importStudentClasses = async (file: File) => {
        try {
            loading.value = true;
            error.value = null;

            const response = await studentClassApi.import(file);

            if (response.success) {
                await fetchStudentClasses();
                return response.data;
            } else {
                error.value = response.message;
                return null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to import student class enrollments";
            return null;
        } finally {
            loading.value = false;
        }
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1; // Reset to first page when changing per page
    };

    const resetFilters = async () => {
        page.value = 1;
        perPage.value = 15;
        await fetchStudentClasses();
    };

    const clearError = () => {
        error.value = null;
    };

    return {
        // State
        studentClasses,
        meta,
        loading,
        error,
        page,
        perPage,

        // Computed
        totalPages,
        currentPage,
        totalItems,

        // Actions
        fetchStudentClasses,
        fetchStudentClassById,
        createStudentClass,
        updateStudentClass,
        deleteStudentClass,
        activateStudentClass,
        deactivateStudentClass,
        getStudentClassesByStudent,
        getStudentClassesByClass,
        getStudentClassesBySemester,
        bulkCreateStudentClasses,
        importStudentClasses,
        setPage,
        setPerPage,
        resetFilters,
        clearError,
    };
});
